import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePeriodo } from '../../contexts/PeriodoContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Clock, Plus, Trash2, Save, Loader2, Info, Percent, Moon, AlertTriangle, Pencil, FileText, Search, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { formatarDataBR, formatarPeriodo } from '../../utils/dateUtils';
import { InputDataBR } from '../../components/ui/input-data-br';

// Tipos de verba calculadas pelo motor - baseados na NATUREZA, não no percentual
// Os percentuais são configuráveis por empresa via ACT/CCT nas faixas
const TIPOS_VERBA = [
  // Horas normais (separadas diurnas/noturnas)
  { value: 'hora_normal_diurna', label: 'Hora Normal Diurna' },
  { value: 'hora_normal_noturna', label: 'Hora Normal Noturna' },
  
  // Horas extras diurnas por tipo de dia
  { value: 'hora_extra_diurna', label: 'Hora Extra Diurna' },
  { value: 'hora_extra_sabado_diurna', label: 'HE Sábado Diurna' },
  { value: 'hora_extra_domingo_diurna', label: 'HE Domingo Diurna' },
  { value: 'hora_extra_feriado_diurna', label: 'HE Feriado Diurna' },
  
  // Horas extras noturnas por tipo de dia
  { value: 'hora_extra_noturna', label: 'Hora Extra Noturna' },
  { value: 'hora_extra_sabado_noturna', label: 'HE Sábado Noturna' },
  { value: 'hora_extra_domingo_noturna', label: 'HE Domingo Noturna' },
  { value: 'hora_extra_feriado_noturna', label: 'HE Feriado Noturna' },
  
  // Adicionais e outros
  { value: 'adicional_noturno', label: 'Adicional Noturno' },
  { value: 'reducao_hora_noturna', label: 'Redução Hora Noturna' },
  { value: 'dsr', label: 'DSR' },
  { value: 'falta', label: 'Falta' },
  { value: 'atraso', label: 'Atraso' },
  { value: 'saida_antecipada', label: 'Saída Antecipada' },
  { value: 'intrajornada_suprimida', label: 'Intrajornada Suprimida' },
  { value: 'interjornada_suprimida', label: 'Interjornada Suprimida' },
  
  // Atestados e afastamentos
  { value: 'atestado_medico', label: 'Atestado Médico' },
  { value: 'licenca_maternidade', label: 'Licença Maternidade' },
  { value: 'licenca_paternidade', label: 'Licença Paternidade' },
  { value: 'licenca_casamento', label: 'Licença Casamento' },
  { value: 'licenca_luto', label: 'Licença Luto' },
  { value: 'licenca_outros', label: 'Outras Licenças' },
];

const HorasExtrasPage = () => {
  const { api, user } = useAuth();
  const { periodoSelecionado } = usePeriodo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabela, setTabela] = useState(null);
  const [showNovaFaixa, setShowNovaFaixa] = useState(false);
  const [showEditarFaixa, setShowEditarFaixa] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('config');
  
  // Estados do relatório
  const [relatorioLoading, setRelatorioLoading] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [sindicatos, setSindicatos] = useState([]);
  
  const [novaFaixa, setNovaFaixa] = useState({
    descricao: '',
    tipo_dia: 'normal',
    hora_inicio: 1,
    hora_fim: null,
    percentual: 50,
    adicional_noturno: false,
    tipo_verba: '',
    tipos_verbas: [],  // Array para múltiplas verbas
    evento_folha: '',
    sindicato_id: ''
  });
  const [faixaEditando, setFaixaEditando] = useState({
    descricao: '',
    tipo_dia: 'normal',
    hora_inicio: 1,
    hora_fim: null,
    percentual: 50,
    adicional_noturno: false,
    tipo_verba: '',
    tipos_verbas: [],  // Array para múltiplas verbas
    evento_folha: '',
    sindicato_id: ''
  });

  // Sincronizar com período global
  useEffect(() => {
    if (periodoSelecionado?.data_inicio && periodoSelecionado?.data_fim) {
      setDataInicio(periodoSelecionado.data_inicio);
      setDataFim(periodoSelecionado.data_fim);
    }
  }, [periodoSelecionado]);

  useEffect(() => {
    fetchTabela();
    fetchSindicatos();
  }, []);

  const fetchTabela = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/tabela-horas-extras?empresa_id=${user?.empresa_id}`);
      setTabela(response.data);
    } catch (error) {
      console.error('Error fetching tabela:', error);
      toast.error('Erro ao carregar tabela de horas extras');
    } finally {
      setLoading(false);
    }
  };

  const fetchSindicatos = async () => {
    try {
      const response = await api.get('/sindicatos');
      setSindicatos(response.data.filter(s => s.ativo !== false));
    } catch (error) {
      console.error('Error fetching sindicatos:', error);
    }
  };

  const salvarTabela = async () => {
    setSaving(true);
    try {
      await api.post('/tabela-horas-extras', {
        ...tabela,
        empresa_id: user?.empresa_id
      });
      toast.success('Tabela salva com sucesso!');
      fetchTabela();
    } catch (error) {
      toast.error('Erro ao salvar tabela');
    } finally {
      setSaving(false);
    }
  };

  const adicionarFaixa = async () => {
    if (!novaFaixa.descricao || novaFaixa.percentual === '' || novaFaixa.percentual === null || novaFaixa.percentual === undefined) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      await api.post(`/tabela-horas-extras/adicionar-faixa?empresa_id=${user?.empresa_id}`, novaFaixa);
      toast.success('Faixa adicionada!');
      setShowNovaFaixa(false);
      setNovaFaixa({
        descricao: '',
        tipo_dia: 'normal',
        hora_inicio: 1,
        hora_fim: null,
        percentual: 50,
        adicional_noturno: false,
        tipo_verba: '',
        evento_folha: '',
        sindicato_id: ''
      });
      fetchTabela();
    } catch (error) {
      toast.error('Erro ao adicionar faixa');
    }
  };

  const abrirEditarFaixa = (index) => {
    const faixa = tabela.faixas[index];
    setFaixaEditando({...faixa});
    setEditIndex(index);
    setShowEditarFaixa(true);
  };

  const salvarEdicaoFaixa = async () => {
    if (!faixaEditando.descricao || faixaEditando.percentual === '' || faixaEditando.percentual === null || faixaEditando.percentual === undefined) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    // Update locally
    const novasFaixas = [...tabela.faixas];
    novasFaixas[editIndex] = faixaEditando;
    
    try {
      await api.post('/tabela-horas-extras', {
        ...tabela,
        faixas: novasFaixas,
        empresa_id: user?.empresa_id
      });
      toast.success('Faixa atualizada!');
      setShowEditarFaixa(false);
      setEditIndex(null);
      fetchTabela();
    } catch (error) {
      toast.error('Erro ao atualizar faixa');
    }
  };

  const removerFaixa = async (indice) => {
    if (!window.confirm('Confirma a remoção desta faixa?')) return;

    try {
      await api.delete(`/tabela-horas-extras/remover-faixa?empresa_id=${user?.empresa_id}&indice=${indice}`);
      toast.success('Faixa removida');
      fetchTabela();
    } catch (error) {
      toast.error('Erro ao remover faixa');
    }
  };

  const getTipoDiaBadge = (tipo) => {
    const tipos = {
      normal: { label: 'Dia Normal', className: 'bg-blue-500' },
      sabado: { label: 'Sábado', className: 'bg-purple-500' },
      domingo: { label: 'Domingo', className: 'bg-amber-500' },
      feriado: { label: 'Feriado', className: 'bg-red-500' }
    };
    const config = tipos[tipo] || tipos.normal;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Função para buscar relatório de verbas
  const fetchRelatorio = async () => {
    if (!dataInicio || !dataFim) {
      toast.error('Selecione o período');
      return;
    }
    
    setRelatorioLoading(true);
    try {
      const response = await api.get(`/relatorio-verbas?data_inicio=${dataInicio}&data_fim=${dataFim}`);
      setRelatorio(response.data);
      if (response.data.total_registros === 0) {
        toast.info('Nenhuma verba encontrada no período');
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setRelatorioLoading(false);
    }
  };

  // Função para exportar CSV
  const exportarCSV = () => {
    if (!relatorio || relatorio.relatorio.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    
    const headers = ['Matrícula', 'Nome', 'Evento', 'Quantidade'];
    const rows = relatorio.relatorio.map(item => [
      item.matricula || '',
      item.nome || '',
      item.evento || '',
      item.quantidade_formatada || ''
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_verbas_${dataInicio}_${dataFim}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('Relatório exportado!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="horas-extras-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-1">Config. Eventos</h2>
          <p className="text-slate-500">Configure percentuais e gere relatórios de verbas</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="config" data-testid="tab-config">
            <Percent className="w-4 h-4 mr-2" />
            Configuração
          </TabsTrigger>
          <TabsTrigger value="relatorio" data-testid="tab-relatorio">
            <FileText className="w-4 h-4 mr-2" />
            Relatório de Verbas
          </TabsTrigger>
        </TabsList>

        {/* ABA CONFIGURAÇÃO */}
        <TabsContent value="config" className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setShowNovaFaixa(true)}
              variant="outline"
              data-testid="btn-nova-faixa"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Faixa
            </Button>
            <Button
              onClick={salvarTabela}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-salvar-tabela"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Salvando...' : 'Salvar Tabela'}
            </Button>
          </div>

          {/* Info Card */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Como funciona a tabela de horas extras?</p>
                  <p>
                    As faixas definem os percentuais de acréscimo sobre a hora normal para cada tipo de dia.
                    Exemplo: 50% significa que a hora extra vale 1,5x a hora normal.
                    Horas extras que excedem o limite diário do banco de horas serão calculadas usando esta tabela para pagamento.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Faixas de Horas Extras */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                <Percent className="w-5 h-5" />
                Faixas de Percentuais
              </CardTitle>
              <CardDescription>
                Percentuais aplicados sobre a hora normal para cálculo de horas extras
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tabela?.faixas?.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-500">Nenhuma faixa configurada</p>
                  <p className="text-sm text-slate-400 mt-1">Adicione faixas de horas extras</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Tipo de Dia</TableHead>
                        <TableHead className="text-center">Hora Início</TableHead>
                        <TableHead className="text-center">Hora Fim</TableHead>
                        <TableHead className="text-center">Percentual</TableHead>
                        <TableHead className="text-center">Ad. Noturno</TableHead>
                        <TableHead className="text-center">Verba</TableHead>
                        <TableHead className="text-center">Evento</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                </TableHeader>
                <TableBody>
                  {tabela?.faixas?.map((faixa, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{faixa.descricao}</TableCell>
                      <TableCell>{getTipoDiaBadge(faixa.tipo_dia)}</TableCell>
                      <TableCell className="text-center">{faixa.hora_inicio}ª hora</TableCell>
                      <TableCell className="text-center">
                        {faixa.hora_fim ? `${faixa.hora_fim}ª hora` : 'Ilimitado'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-semibold text-emerald-600">
                          +{faixa.percentual}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {faixa.adicional_noturno ? (
                          <Moon className="w-4 h-4 text-indigo-500 mx-auto" />
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {faixa.tipos_verbas && faixa.tipos_verbas.length > 0 ? (
                          <div className="flex flex-wrap gap-1 justify-center">
                            {faixa.tipos_verbas.slice(0, 2).map(tv => (
                              <Badge key={tv} variant="secondary" className="text-xs">
                                {TIPOS_VERBA.find(t => t.value === tv)?.label || tv}
                              </Badge>
                            ))}
                            {faixa.tipos_verbas.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{faixa.tipos_verbas.length - 2}</Badge>
                            )}
                          </div>
                        ) : faixa.tipo_verba ? (
                          <Badge variant="secondary" className="text-xs">
                            {TIPOS_VERBA.find(t => t.value === faixa.tipo_verba)?.label || faixa.tipo_verba}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {faixa.evento_folha ? (
                          <Badge variant="outline" className="font-mono">{faixa.evento_folha}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => abrirEditarFaixa(index)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`btn-editar-faixa-${index}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removerFaixa(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-remover-faixa-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configurações Adicionais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <Moon className="w-5 h-5" />
              Adicional Noturno
            </CardTitle>
            <CardDescription>Configurações para trabalho noturno (22h às 5h)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Percentual Adicional Noturno (%)</Label>
              <Input
                type="number"
                value={tabela?.percentual_adicional_noturno || 20}
                onChange={(e) => setTabela({
                  ...tabela,
                  percentual_adicional_noturno: parseFloat(e.target.value)
                })}
                data-testid="input-adicional-noturno"
              />
              <p className="text-xs text-slate-500">CLT: 20% sobre a hora diurna</p>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Hora Noturna Reduzida</p>
                <p className="text-sm text-slate-500">52min30seg = 1 hora (CLT Art. 73)</p>
              </div>
              <Switch
                checked={tabela?.hora_noturna_reduzida || true}
                onCheckedChange={(checked) => setTabela({
                  ...tabela,
                  hora_noturna_reduzida: checked
                })}
                data-testid="switch-hora-reduzida"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Intervalos Suprimidos
            </CardTitle>
            <CardDescription>Penalidades por intervalos não concedidos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Intrajornada Suprimida (%)</Label>
              <Input
                type="number"
                value={tabela?.percentual_intrajornada_suprimida || 50}
                onChange={(e) => setTabela({
                  ...tabela,
                  percentual_intrajornada_suprimida: parseFloat(e.target.value)
                })}
                data-testid="input-intrajornada"
              />
              <p className="text-xs text-slate-500">Hora cheia + percentual (CLT Art. 71 §4º)</p>
            </div>
            <div className="space-y-2">
              <Label>Interjornada Suprimida (%)</Label>
              <Input
                type="number"
                value={tabela?.percentual_interjornada_suprimida || 50}
                onChange={(e) => setTabela({
                  ...tabela,
                  percentual_interjornada_suprimida: parseFloat(e.target.value)
                })}
                data-testid="input-interjornada"
              />
              <p className="text-xs text-slate-500">Mínimo 11h entre jornadas (CLT Art. 66)</p>
            </div>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        {/* ABA RELATÓRIO */}
        <TabsContent value="relatorio" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Filtros do Relatório
              </CardTitle>
              <CardDescription>
                Selecione o período para gerar o relatório de verbas por empregado
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 items-end flex-wrap">
                <div>
                  <Label>Data Início</Label>
                  <InputDataBR
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-36"
                    data-testid="input-data-inicio"
                  />
                </div>
                <div>
                  <Label>Data Fim</Label>
                  <InputDataBR
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-36"
                    data-testid="input-data-fim"
                  />
                </div>
                <Button onClick={fetchRelatorio} disabled={relatorioLoading} data-testid="btn-buscar-relatorio">
                  {relatorioLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Gerar Relatório
                </Button>
                {relatorio && relatorio.total_registros > 0 && (
                  <Button variant="outline" onClick={exportarCSV} data-testid="btn-exportar-csv">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {relatorio && (
            <>
              {/* Resumo */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Verbas com Evento</p>
                      <p className="text-3xl font-bold text-emerald-600">{relatorio.total_registros}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Passivo Trabalhista</p>
                      <p className="text-3xl font-bold text-amber-600">{relatorio.total_passivo}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Faixas Configuradas</p>
                      <p className="text-3xl font-bold text-blue-600">{relatorio.faixas_configuradas}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tabela do Relatório */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Relatório de Verbas por Empregado
                  </CardTitle>
                  <CardDescription>
                    Verbas calculadas com evento de folha configurado
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {relatorio.relatorio.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Nenhuma verba com evento configurado encontrada</p>
                      <p className="text-sm mt-1">Configure os tipos de verba e eventos nas faixas de percentuais</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Matrícula</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead className="text-center">Evento</TableHead>
                            <TableHead className="text-right">Quantidade</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {relatorio.relatorio.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono">{item.matricula || '-'}</TableCell>
                              <TableCell>{item.nome}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="font-mono">{item.evento}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold">
                                {item.quantidade_formatada}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Passivo Trabalhista */}
              {relatorio.passivo_trabalhista.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                      Passivo Trabalhista
                    </CardTitle>
                    <CardDescription>
                      Verbas calculadas sem evento de folha configurado - não serão enviadas para folha
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Matrícula</TableHead>
                            <TableHead>Nome</TableHead>
                            <TableHead>Tipo de Verba</TableHead>
                            <TableHead className="text-right">Quantidade</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {relatorio.passivo_trabalhista.map((item, idx) => (
                            <TableRow key={idx} className="bg-amber-50">
                              <TableCell className="font-mono">{item.matricula || '-'}</TableCell>
                              <TableCell>{item.nome}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="border-amber-500 text-amber-700">
                                  {item.descricao}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold">
                                {item.quantidade_formatada}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Nova Faixa - Com Sidebar de Navegação */}
      <Dialog open={showNovaFaixa} onOpenChange={setShowNovaFaixa}>
        <DialogContent className="sm:max-w-[700px] p-0">
          <div className="flex">
            {/* Sidebar de Navegação */}
            <div className="w-48 bg-slate-50 border-r p-4 space-y-1 rounded-l-lg">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Seções</p>
              <button 
                type="button"
                className="w-full text-left px-3 py-2 text-sm rounded-md bg-accent text-white"
              >
                Dados Básicos
              </button>
              <button 
                type="button"
                className="w-full text-left px-3 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-100"
              >
                Faixa de Horas
              </button>
              <button 
                type="button"
                className="w-full text-left px-3 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-100"
              >
                Integração Folha
              </button>
              <button 
                type="button"
                className="w-full text-left px-3 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-100"
              >
                Sindicato
              </button>
            </div>
            
            {/* Conteúdo Principal */}
            <div className="flex-1 p-6">
              <DialogHeader className="mb-4">
                <DialogTitle>Nova Faixa de Hora Extra</DialogTitle>
                <DialogDescription>
                  Configure uma regra de percentual para horas extras
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                {/* Seção: Dados Básicos */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">1</span>
                    Dados Básicos
                  </h4>
                  <div className="space-y-2">
                    <Label>Descrição *</Label>
                    <Input
                      placeholder="Ex: Horas extras 50% em dias normais"
                      value={novaFaixa.descricao}
                      onChange={(e) => setNovaFaixa({...novaFaixa, descricao: e.target.value})}
                      data-testid="input-descricao-faixa"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Dia</Label>
                      <Select
                        value={novaFaixa.tipo_dia}
                        onValueChange={(v) => setNovaFaixa({...novaFaixa, tipo_dia: v})}
                      >
                        <SelectTrigger data-testid="select-tipo-dia">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Dia Normal</SelectItem>
                          <SelectItem value="sabado">Sábado</SelectItem>
                          <SelectItem value="domingo">Domingo</SelectItem>
                          <SelectItem value="feriado">Feriado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Percentual (%)</Label>
                      <Input
                        type="number"
                        placeholder="50"
                        value={novaFaixa.percentual}
                        onChange={(e) => setNovaFaixa({...novaFaixa, percentual: parseFloat(e.target.value)})}
                        data-testid="input-percentual-faixa"
                      />
                    </div>
                  </div>
                </div>

                {/* Seção: Faixa de Horas */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">2</span>
                    Faixa de Horas
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>A partir da Hora Extra</Label>
                      <Input
                        type="number"
                        min="1"
                        value={novaFaixa.hora_inicio}
                        onChange={(e) => setNovaFaixa({...novaFaixa, hora_inicio: parseInt(e.target.value)})}
                        data-testid="input-hora-inicio"
                      />
                      <p className="text-xs text-slate-500">Ex: 1 = 1ª hora extra</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Até a Hora (opcional)</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Ilimitado"
                        value={novaFaixa.hora_fim || ''}
                        onChange={(e) => setNovaFaixa({...novaFaixa, hora_fim: e.target.value ? parseInt(e.target.value) : null})}
                        data-testid="input-hora-fim"
                      />
                      <p className="text-xs text-slate-500">Deixe vazio para ilimitado</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Inclui Adicional Noturno</p>
                      <p className="text-xs text-slate-500">Acumula adicional noturno (22h-05h)</p>
                    </div>
                    <Switch
                      checked={novaFaixa.adicional_noturno}
                      onCheckedChange={(checked) => setNovaFaixa({...novaFaixa, adicional_noturno: checked})}
                      data-testid="switch-adicional-noturno-faixa"
                    />
                  </div>
                </div>

                {/* Seção: Integração Folha */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">3</span>
                    Integração com Folha
                  </h4>
                  <div className="space-y-2">
                    <Label>Tipos de Verbas (múltipla seleção)</Label>
                    <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                      {TIPOS_VERBA.map(tipo => (
                        <label key={tipo.value} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={(novaFaixa.tipos_verbas || []).includes(tipo.value)}
                            onChange={(e) => {
                              const atual = novaFaixa.tipos_verbas || [];
                              if (e.target.checked) {
                                setNovaFaixa({...novaFaixa, tipos_verbas: [...atual, tipo.value]});
                              } else {
                                setNovaFaixa({...novaFaixa, tipos_verbas: atual.filter(v => v !== tipo.value)});
                              }
                            }}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">{tipo.label}</span>
                        </label>
                      ))}
                    </div>
                    {(novaFaixa.tipos_verbas || []).length > 0 && (
                      <p className="text-xs text-emerald-600">
                        {(novaFaixa.tipos_verbas || []).length} verba(s) selecionada(s)
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Código do Evento</Label>
                    <Input
                      placeholder="Ex: 1001, E001"
                      value={novaFaixa.evento_folha}
                      onChange={(e) => setNovaFaixa({...novaFaixa, evento_folha: e.target.value})}
                      data-testid="input-evento-folha-faixa"
                    />
                    <p className="text-xs text-slate-500">Código para exportar na folha de pagamento</p>
                  </div>
                </div>

                {/* Seção: Sindicato */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">4</span>
                    Sindicato/Categoria
                  </h4>
                  <div className="space-y-2">
                    <Label>Aplicar apenas para</Label>
                    <Select
                      value={novaFaixa.sindicato_id || "none"}
                      onValueChange={(v) => setNovaFaixa({...novaFaixa, sindicato_id: v === "none" ? "" : v})}
                    >
                      <SelectTrigger data-testid="select-sindicato-faixa">
                        <SelectValue placeholder="Selecione (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Todos os empregados</SelectItem>
                        {sindicatos.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.nome} {s.sigla ? `(${s.sigla})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Deixe vazio para aplicar a todos os empregados</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowNovaFaixa(false)}>
                  Cancelar
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={adicionarFaixa}
                  data-testid="btn-confirmar-faixa"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Faixa
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Faixa */}
      <Dialog open={showEditarFaixa} onOpenChange={setShowEditarFaixa}>
        <DialogContent className="sm:max-w-[700px] p-0">
          <div className="flex">
            {/* Sidebar de Navegação */}
            <div className="w-48 bg-slate-50 border-r p-4 space-y-1 rounded-l-lg">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Seções</p>
              <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md bg-accent text-white">
                Dados Básicos
              </button>
              <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-100">
                Faixa de Horas
              </button>
              <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-100">
                Integração Folha
              </button>
              <button type="button" className="w-full text-left px-3 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-100">
                Sindicato
              </button>
            </div>
            
            {/* Conteúdo Principal */}
            <div className="flex-1 p-6">
              <DialogHeader className="mb-4">
                <DialogTitle>Alterar Faixa de Hora Extra</DialogTitle>
                <DialogDescription>Edite os dados da faixa de hora extra</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                {/* Seção: Dados Básicos */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">1</span>
                    Dados Básicos
                  </h4>
                  <div className="space-y-2">
                    <Label>Descrição *</Label>
                    <Input
                      placeholder="Ex: Horas extras 50% em dias normais"
                      value={faixaEditando.descricao}
                      onChange={(e) => setFaixaEditando({...faixaEditando, descricao: e.target.value})}
                      data-testid="input-descricao-faixa-edit"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Dia</Label>
                      <Select
                        value={faixaEditando.tipo_dia}
                        onValueChange={(v) => setFaixaEditando({...faixaEditando, tipo_dia: v})}
                      >
                        <SelectTrigger data-testid="select-tipo-dia-edit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Dia Normal</SelectItem>
                          <SelectItem value="sabado">Sábado</SelectItem>
                          <SelectItem value="domingo">Domingo</SelectItem>
                          <SelectItem value="feriado">Feriado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Percentual (%)</Label>
                      <Input
                        type="number"
                        placeholder="50"
                        value={faixaEditando.percentual}
                        onChange={(e) => setFaixaEditando({...faixaEditando, percentual: parseFloat(e.target.value)})}
                        data-testid="input-percentual-faixa-edit"
                      />
                    </div>
                  </div>
                </div>

                {/* Seção: Faixa de Horas */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">2</span>
                    Faixa de Horas
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>A partir da Hora Extra</Label>
                      <Input
                        type="number"
                        min="1"
                        value={faixaEditando.hora_inicio}
                        onChange={(e) => setFaixaEditando({...faixaEditando, hora_inicio: parseInt(e.target.value)})}
                        data-testid="input-hora-inicio-edit"
                      />
                      <p className="text-xs text-slate-500">Ex: 1 = 1ª hora extra</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Até a Hora (opcional)</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Ilimitado"
                        value={faixaEditando.hora_fim || ''}
                        onChange={(e) => setFaixaEditando({...faixaEditando, hora_fim: e.target.value ? parseInt(e.target.value) : null})}
                        data-testid="input-hora-fim-edit"
                      />
                      <p className="text-xs text-slate-500">Deixe vazio para ilimitado</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Inclui Adicional Noturno</p>
                      <p className="text-xs text-slate-500">Acumula adicional noturno (22h-05h)</p>
                    </div>
                    <Switch
                      checked={faixaEditando.adicional_noturno}
                      onCheckedChange={(checked) => setFaixaEditando({...faixaEditando, adicional_noturno: checked})}
                      data-testid="switch-adicional-noturno-faixa-edit"
                    />
                  </div>
                </div>

                {/* Seção: Integração Folha */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">3</span>
                    Integração com Folha
                  </h4>
                  <div className="space-y-2">
                    <Label>Tipos de Verbas (múltipla seleção)</Label>
                    <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                      {TIPOS_VERBA.map(tipo => (
                        <label key={tipo.value} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={(faixaEditando.tipos_verbas || []).includes(tipo.value)}
                            onChange={(e) => {
                              const atual = faixaEditando.tipos_verbas || [];
                              if (e.target.checked) {
                                setFaixaEditando({...faixaEditando, tipos_verbas: [...atual, tipo.value]});
                              } else {
                                setFaixaEditando({...faixaEditando, tipos_verbas: atual.filter(v => v !== tipo.value)});
                              }
                            }}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">{tipo.label}</span>
                        </label>
                      ))}
                    </div>
                    {(faixaEditando.tipos_verbas || []).length > 0 && (
                      <p className="text-xs text-emerald-600">
                        {(faixaEditando.tipos_verbas || []).length} verba(s) selecionada(s)
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Código do Evento</Label>
                    <Input
                      placeholder="Ex: 1001, E001"
                      value={faixaEditando.evento_folha || ''}
                      onChange={(e) => setFaixaEditando({...faixaEditando, evento_folha: e.target.value})}
                      data-testid="input-evento-folha-faixa-edit"
                    />
                    <p className="text-xs text-slate-500">Código para exportar na folha de pagamento</p>
                  </div>
                </div>

                {/* Seção: Sindicato */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center">4</span>
                    Sindicato/Categoria
                  </h4>
                  <div className="space-y-2">
                    <Label>Aplicar apenas para</Label>
                    <Select
                      value={faixaEditando.sindicato_id || "todos"}
                      onValueChange={(v) => setFaixaEditando({...faixaEditando, sindicato_id: v === "todos" ? null : v})}
                    >
                      <SelectTrigger data-testid="select-sindicato-edit">
                        <SelectValue placeholder="Todos os empregados" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os empregados</SelectItem>
                        {sindicatos.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Se vazio, vale para todos os empregados</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditarFaixa(false)}>
                  Cancelar
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={salvarEdicaoFaixa}
                  data-testid="btn-salvar-edicao-faixa"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HorasExtrasPage;
